/*eslint max-lines-per-function: ["error", 180]*/
import React, { useState } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import Select from 'react-select';

import { CreateUpdateQuestionDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import { useTranslation } from '../../../common/hooks/useTranslation';
import QuillTextEditor from '../../../app/components/QuillTextEditor';
import {
  QuestionResponseTypeEnum,
  QuestionResponseTypeObj,
  YesNoEnum,
  YesNoObj,
} from '../../../common/pokCore/validation/enums';
import { validateQuestion } from '../../../common/pokCore/contexts/SurveyContext';
import ValidationAlert from '../../../app/components/ValidationAlert';

interface QuestionEditorProps {
  show: boolean;
  question: CreateUpdateQuestionDto;
  onSave: (question: CreateUpdateQuestionDto) => void;
  onClose: () => void;
}

export const QuestionEditor: React.FC<QuestionEditorProps> = ({
  show,
  question: initialQuestion,
  onSave,
  onClose,
}) => {
  const { t, tk } = useTranslation('administrationLng');
  const [question, setQuestion] =
    useState<CreateUpdateQuestionDto>(initialQuestion);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  const responseTypeOptions = Object.entries(QuestionResponseTypeEnum).map(
    ([key, value]) => ({
      value: key as QuestionResponseTypeEnum,
      label: t(QuestionResponseTypeObj[value]),
    }),
  );

  const yesNoTypeOptions = Object.entries(YesNoEnum).map(([key, value]) => ({
    value: key as YesNoEnum,
    label: t(YesNoObj[value]),
  }));

  const propertyChange = (obj: Partial<CreateUpdateQuestionDto>) => {
    if (
      ('expectedYesNoAnswer' in obj && obj.expectedYesNoAnswer === undefined) ||
      obj.responseType === QuestionResponseTypeEnum.Text
    ) {
      obj.blockOnWrongAnswer = false;
      obj.warningLight = false;
    }
    if (obj.responseType === QuestionResponseTypeEnum.Text) {
      obj.expectedYesNoAnswer = undefined;
    }
    setQuestion({ ...question, ...obj });
  };

  const handleSave = async () => {
    const validation = await validateQuestion(question);
    if (!validation.valid) {
      setSaveErrors(validation.errors);
      setShowSaveErrors(true);
      return;
    }
    setSaveErrors([]);
    setShowSaveErrors(false);
    onSave(question);
  };

  const isYesNoType =
    question.responseType === QuestionResponseTypeEnum.YesNo ||
    question.responseType === QuestionResponseTypeEnum.YesNoText;

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      backdrop="static"
      unmountOnClose={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {initialQuestion.question
            ? t(tk.survey.questionEdit)
            : t(tk.survey.questionAdd)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="d-grid gap-3">
          <FormRow controlId="question" label={t(tk.survey.question)}>
            <QuillTextEditor
              value={question.question || ''}
              onChange={val => propertyChange({ question: val })}
              uniqueKey="questionPl"
            />
          </FormRow>

          <FormRow controlId="questionEn" label={t(tk.survey.questionEn)}>
            <QuillTextEditor
              value={question.questionEn || ''}
              onChange={val => propertyChange({ questionEn: val })}
              uniqueKey="questionEn"
            />
          </FormRow>

          <FormRow controlId="responseType" label={t(tk.survey.responseType)}>
            <Select
              value={responseTypeOptions.find(
                o => o.value === question.responseType,
              )}
              onChange={option =>
                propertyChange({ responseType: option?.value })
              }
              options={responseTypeOptions}
            />
          </FormRow>

          <FormRow controlId="optional" label={t(tk.survey.optional)}>
            <Form.Check
              type="checkbox"
              className="pt-2"
              checked={question.optional}
              onChange={e => propertyChange({ optional: e.target.checked })}
            />
          </FormRow>

          {isYesNoType && (
            <>
              <FormRow
                controlId="expectedYesNoAnswer"
                label={t(tk.survey.expectedYesNoAnswer)}
              >
                <Select
                  value={yesNoTypeOptions.find(
                    o => o.value === question.expectedYesNoAnswer,
                  )}
                  onChange={option =>
                    propertyChange({ expectedYesNoAnswer: option?.value })
                  }
                  options={yesNoTypeOptions}
                  placeholder={t(tk.component.pointOut)}
                  isClearable={true}
                />
              </FormRow>
              {question.expectedYesNoAnswer !== undefined &&
                question.expectedYesNoAnswer !== null && (
                  <>
                    <FormRow
                      controlId="blockOnWrongAnswer"
                      label={t(tk.survey.blockOnWrongAnswer)}
                    >
                      <Form.Check
                        type="checkbox"
                        className="pt-2"
                        checked={question.blockOnWrongAnswer === true}
                        onChange={e =>
                          propertyChange({
                            blockOnWrongAnswer: e.target.checked,
                          })
                        }
                      />
                    </FormRow>
                    <FormRow
                      controlId="warningLight"
                      label={t(tk.survey.warningLight)}
                    >
                      <Form.Check
                        type="checkbox"
                        className="pt-2"
                        checked={question.warningLight}
                        onChange={e =>
                          propertyChange({ warningLight: e.target.checked })
                        }
                      />
                    </FormRow>
                  </>
                )}
            </>
          )}
        </Form>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t(tk.button.cancel)}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t(tk.button.save)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
